import React from "react";
import { ShieldCheck, UserCheck, FileOutput, Settings } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function GDPR() {
  return (
    <>
      <Helmet>
        <title>GDPR Policy | Lo-Fee.com</title>
        <meta
          name="description"
          content="Learn how Lo-Fee.com complies with GDPR regulations and protects your privacy rights when comparing Bitcoin exchange fees in Canada."
        />
        <link rel="canonical" href="https://lo-fee.com/gdpr" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">GDPR Policy</h1>
            <p className="text-lg text-[--text-secondary]">
              How Lo-Fee.com complies with the General Data Protection
              Regulation (GDPR) and protects your privacy rights.
            </p>
          </header>

          <section className="grid sm:grid-cols-2 gap-6">
            {[
              {
                icon: <ShieldCheck className="h-6 w-6 text-blue-500" />,
                title: "Data Protection",
                description:
                  "We implement appropriate technical and organizational measures to protect your data.",
              },
              {
                icon: <UserCheck className="h-6 w-6 text-green-500" />,
                title: "User Rights",
                description:
                  "You have full control over your data with rights to access, modify, or delete it.",
              },
              {
                icon: <FileOutput className="h-6 w-6 text-purple-500" />,
                title: "Data Portability",
                description:
                  "Request and receive your data in a machine-readable format.",
              },
              {
                icon: <Settings className="h-6 w-6 text-orange-500" />,
                title: "Privacy by Design",
                description:
                  "Data protection is built into our systems from the ground up.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="p-6 bg-[--bg-secondary] border-2 border-[--border-color]"
              >
                <div className="flex gap-4">
                  {feature.icon}
                  <div>
                    <h3 className="font-bold mb-2">{feature.title}</h3>
                    <p className="text-[--text-secondary]">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Your GDPR Rights</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>Under GDPR, you have the following rights:</p>
              <ul>
                <li>
                  <strong>Right to Access:</strong> Request copies of your
                  personal data
                </li>
                <li>
                  <strong>Right to Rectification:</strong> Correct inaccurate
                  information
                </li>
                <li>
                  <strong>Right to Erasure:</strong> Request deletion of your
                  data
                </li>
                <li>
                  <strong>Right to Restrict Processing:</strong> Limit how we
                  use your data
                </li>
                <li>
                  <strong>Right to Data Portability:</strong> Transfer your data
                  elsewhere
                </li>
                <li>
                  <strong>Right to Object:</strong> Stop processing of your data
                </li>
              </ul>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Data Processing</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-bold">Legal Basis</h3>
                <ul className="space-y-2 text-[--text-secondary]">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Legitimate
                    interests
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Contract
                    performance
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Legal obligations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> User consent
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-bold">Data Retention</h3>
                <ul className="space-y-2 text-[--text-secondary]">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Minimal data
                    collection
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Limited retention
                    periods
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Regular data
                    reviews
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">•</span> Secure deletion
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">International Transfers</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>
                While Lo-Fee.com is based in Canada, we ensure GDPR compliance
                for all users, regardless of location. Any international data
                transfers are protected by appropriate safeguards.
              </p>
            </div>
          </section>

          <section className="bg-[--bg-secondary] border-2 border-[--border-color] p-6">
            <h2 className="text-xl font-bold mb-4">Contact Our DPO</h2>
            <p className="text-[--text-secondary]">
              For any GDPR-related inquiries or to exercise your rights, contact
              us at{" "}
              <a
                href="mailto:contact@lo-fee.com"
                className="text-[--accent] hover:underline"
              >
                contact@lo-fee.com
              </a>
            </p>
            <p className="text-sm text-[--text-secondary] mt-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
